if (!customElements.get('product-store-availability')) {
    customElements.define(
        'product-store-availability',
        class ProductStoreAvailability extends HTMLElement {
            constructor() {
                super();

                this.form = this.querySelector('[data-store-availability-form]');
                this.input = this.querySelector('[data-store-availability-query]');
                this.status = this.querySelector('[data-store-availability-status]');
                this.results = this.querySelector('[data-store-availability-results]');
                this.variantsScript = this.querySelector(
                    '[data-store-availability-variants]'
                );

                this.accordion = this.closest(
                    '[data-product-inline-accordion-item]'
                );

                this.accordionTrigger = this.accordion?.querySelector(
                    '[data-product-inline-accordion-trigger]'
                );

                this.googleApiKey = this.dataset.googleApiKey;
                this.azureApiUrl = this.dataset.azureApiUrl;
                this.azureSubscriptionKey = this.dataset.azureSubscriptionKey;
                this.storeLimit = Number.parseInt(this.dataset.storeLimit, 10) || 3;

                this.hasRequestedLocation = false;
                this.requestController = null;

                this.handleSubmit = this.handleSubmit.bind(this);
                this.handleAccordionClick =
                    this.handleAccordionClick.bind(this);
            }

            connectedCallback() {
                this.variants = this.readVariants();

                this.form?.addEventListener('submit', this.handleSubmit);
                this.accordionTrigger?.addEventListener(
                    'click',
                    this.handleAccordionClick
                );

                if (this.accordionTrigger?.getAttribute('aria-expanded') === 'true') {
                    this.requestUserLocation();
                }
            }

            disconnectedCallback() {
                this.form?.removeEventListener('submit', this.handleSubmit);
                this.accordionTrigger?.removeEventListener(
                    'click',
                    this.handleAccordionClick
                );

                this.requestController?.abort();
            }

            readVariants() {
                if (!this.variantsScript) return [];

                try {
                    const variants = JSON.parse(this.variantsScript.textContent);

                    return variants
                        .map((variant) => this.normalizeVariant(variant))
                        .filter(Boolean);
                } catch (error) {
                    console.error(
                        '[Store availability] Invalid variant JSON:',
                        error
                    );

                    return [];
                }
            }

            normalizeVariant(variant) {
                const sku = String(variant.sku || '').trim();
                const separatorIndex = sku.indexOf('_');

                if (separatorIndex < 1 || separatorIndex === sku.length - 1) {
                    console.warn(
                        `[Store availability] Invalid SKU ignored: ${sku || '(empty)'}`
                    );

                    return null;
                }

                const itemId = sku.slice(0, separatorIndex).trim();
                const variantId = sku.slice(separatorIndex + 1).trim();

                if (!itemId || !variantId) return null;

                return {
                    id: String(variant.id),
                    title: String(variant.title || ''),
                    sku,
                    ItemId: itemId,
                    VariantId: variantId
                };
            }

            getStores() {
                if (!Array.isArray(window.shasaStores)) {
                    return [];
                }

                return window.shasaStores.filter((store) => {
                    return (
                        store.storeCode &&
                        Number.isFinite(Number(store.lat)) &&
                        Number.isFinite(Number(store.lng))
                    );
                });
            }

            handleAccordionClick() {
                window.setTimeout(() => {
                    const isOpen =
                        this.accordionTrigger?.getAttribute('aria-expanded') === 'true';

                    if (isOpen && !this.hasRequestedLocation) {
                        this.requestUserLocation();
                    }
                }, 0);
            }

            handleSubmit(event) {
                event.preventDefault();

                const query = this.input?.value.trim();

                if (!query) {
                    this.setStatus('Escribe una dirección, ciudad o estado.', 'error');
                    this.input?.focus();
                    return;
                }

                this.searchAddress(query);
            }

            async searchAddress(query) {
                this.startRequest('Buscando tiendas cercanas…');

                try {
                    const params = new URLSearchParams({
                        address: `${query}, México`,
                        key: this.googleApiKey
                    });

                    const response = await fetch(
                        `https://maps.googleapis.com/maps/api/geocode/json?${params}`,
                        {
                            signal: this.requestController.signal
                        }
                    );

                    if (!response.ok) {
                        throw new Error(`Google Geocoding HTTP ${response.status}`);
                    }

                    const place = await response.json();

                    if (place.status !== 'OK' || !place.results?.length) {
                        throw new Error(
                            place.error_message || 'No se encontró la ubicación.'
                        );
                    }

                    const location = place.results[0].geometry.location;

                    await this.loadNearbyStores({
                        latitude: Number(location.lat),
                        longitude: Number(location.lng)
                    });
                } catch (error) {
                    this.handleRequestError(
                        error,
                        'No encontramos esa ubicación. Intenta con otra dirección.'
                    );
                }
            }

            requestUserLocation() {
                this.hasRequestedLocation = true;

                if (!navigator.geolocation) {
                    this.setStatus(
                        'Tu navegador no permite obtener tu ubicación. Busca por dirección.',
                        'error'
                    );
                    return;
                }

                this.startRequest('Obteniendo tu ubicación…');

                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        this.loadNearbyStores({
                            latitude: position.coords.latitude,
                            longitude: position.coords.longitude
                        }).catch((error) => {
                            this.handleRequestError(
                                error,
                                'No fue posible consultar la disponibilidad.'
                            );
                        });
                    },
                    (error) => {
                        if (this.requestController?.signal.aborted) return;

                        const messages = {
                            1: 'No autorizaste el acceso a tu ubicación. Puedes buscar por dirección.',
                            2: 'No pudimos determinar tu ubicación. Puedes buscar por dirección.',
                            3: 'La solicitud de ubicación tardó demasiado. Puedes buscar por dirección.'
                        };

                        this.setStatus(
                            messages[error.code] ||
                            'No pudimos obtener tu ubicación. Puedes buscar por dirección.',
                            'error'
                        );
                    },
                    {
                        enableHighAccuracy: false,
                        timeout: 10000,
                        maximumAge: 300000
                    }
                );
            }

            async loadNearbyStores(coords) {
                const stores = this.getStores();

                if (!stores.length) {
                    throw new Error('Store catalog is unavailable.');
                }

                if (!this.variants.length) {
                    throw new Error('No valid product SKUs were found.');
                }

                const maximumDistanceKm = 50;

                const nearbyStores = stores
                    .map((store) => ({
                        ...store,
                        distance: this.calculateDistance(
                            coords.latitude,
                            coords.longitude,
                            Number(store.lat),
                            Number(store.lng)
                        )
                    }))
                    .filter((store) => store.distance <= maximumDistanceKm)
                    .sort((a, b) => a.distance - b.distance)
                    .slice(0, this.storeLimit);

                if (!nearbyStores.length) {
                    this.results.replaceChildren();
                    this.setStatus('');

                    const empty = document.createElement('p');
                    empty.className = 'product-store-availability__empty';
                    empty.textContent =
                        'Sin tiendas disponibles cerca de tu ubicación.';

                    this.results.appendChild(empty);
                    return;
                }

                this.setStatus('Consultando disponibilidad…', 'loading');

                const storeResults = await Promise.allSettled(
                    nearbyStores.map((store) =>
                        this.getStoreInventory(store, this.requestController.signal)
                    )
                );

                if (this.requestController.signal.aborted) return;

                const successfulResults = storeResults
                    .filter((result) => result.status === 'fulfilled')
                    .map((result) => result.value);

                const failedCount = storeResults.length - successfulResults.length;

                this.renderResults(successfulResults);

                if (!successfulResults.length) {
                    throw new Error('All inventory requests failed.');
                }

                if (failedCount > 0) {
                    this.setStatus(
                        'Algunas tiendas no pudieron consultarse.',
                        'warning'
                    );
                    return;
                }

                this.setStatus('');
            }

            async getStoreInventory(store, signal) {
                const items = this.variants.map((variant) => ({
                    ItemId: variant.ItemId,
                    VariantId: variant.VariantId,
                    title: variant.title
                }));

                const response = await fetch(this.azureApiUrl, {
                    method: 'POST',
                    signal,
                    headers: {
                        'Content-Type': 'application/json',
                        'Ocp-Apim-Subscription-Key': this.azureSubscriptionKey
                    },
                    body: JSON.stringify({
                        storeId: store.storeCode,
                        items: items.map(({ ItemId, VariantId }) => ({
                            ItemId,
                            VariantId
                        }))
                    })
                });

                if (!response.ok) {
                    throw new Error(
                        `Inventory request failed for ${store.storeCode}: ${response.status}`
                    );
                }

                const data = await response.json();
                const responseItems = Array.isArray(data.ItemsInStoreGetResult)
                    ? data.ItemsInStoreGetResult
                    : [];

                const availableVariants = items.filter((variant) => {
                    const inventoryItem = responseItems.find((item) => {
                        return (
                            String(item.ItemId) === String(variant.ItemId) &&
                            String(item.VariantId) === String(variant.VariantId)
                        );
                    });

                    const quantity = Number.parseInt(
                        inventoryItem?.QtyInventory,
                        10
                    );

                    return Number.isFinite(quantity) && quantity > 0;
                });

                return {
                    store,
                    availableVariants
                };
            }

            renderResults(storeResults) {
                this.results.replaceChildren();

                const storesWithInventory = storeResults.filter(
                    ({ availableVariants }) => availableVariants.length > 0
                );

                if (!storesWithInventory.length) {
                    const empty = document.createElement('p');
                    empty.className = 'product-store-availability__empty';
                    empty.textContent =
                        'Sin inventario disponible cerca de tu ubicación.';

                    this.results.appendChild(empty);
                    return;
                }

                const fragment = document.createDocumentFragment();

                storesWithInventory.forEach(({ store, availableVariants }) => {
                    const storeElement = document.createElement('article');
                    storeElement.className = 'product-store-availability__store';

                    const heading = document.createElement('h4');
                    heading.className = 'product-store-availability__store-name';
                    heading.textContent = store.name;

                    const distance = document.createElement('p');
                    distance.className = 'product-store-availability__distance';
                    distance.textContent = `${this.formatDistance(store.distance)} km`;

                    const headingRow = document.createElement('div');
                    headingRow.className =
                        'product-store-availability__store-heading';

                    headingRow.append(heading, distance);

                    const address = document.createElement('p');
                    address.className = 'product-store-availability__address';
                    address.textContent = [
                        store.address,
                        store.addressTwo,
                        store.municipio,
                        store.estado,
                        store.zipCode
                    ]
                        .filter(Boolean)
                        .join(', ');

                    const sizes = document.createElement('div');
                    sizes.className = 'product-store-availability__sizes';

                    availableVariants.forEach((variant) => {
                        const size = document.createElement('span');
                        size.className = 'product-store-availability__size';
                        size.textContent = variant.title;

                        sizes.appendChild(size);
                    });

                    storeElement.append(headingRow, address, sizes);
                    fragment.appendChild(storeElement);
                });

                this.results.appendChild(fragment);
            }

            startRequest(message) {
                this.requestController?.abort();
                this.requestController = new AbortController();

                this.results.replaceChildren();
                this.setStatus(message, 'loading');
            }

            handleRequestError(error, publicMessage) {
                if (error?.name === 'AbortError') return;

                console.error('[Store availability]', error);
                this.results.replaceChildren();
                this.setStatus(publicMessage, 'error');
            }

            setStatus(message, type = '') {
                if (!this.status) return;

                this.status.textContent = message;
                this.status.dataset.statusType = type;
                this.status.hidden = !message;
            }

            formatDistance(distance) {
                if (distance < 10) {
                    return distance.toFixed(1);
                }

                return Math.round(distance).toString();
            }

            calculateDistance(lat1, lng1, lat2, lng2) {
                const earthRadiusKm = 6371;
                const toRadians = (degrees) => (degrees * Math.PI) / 180;

                const latitudeDifference = toRadians(lat2 - lat1);
                const longitudeDifference = toRadians(lng2 - lng1);

                const a =
                    Math.sin(latitudeDifference / 2) ** 2 +
                    Math.cos(toRadians(lat1)) *
                    Math.cos(toRadians(lat2)) *
                    Math.sin(longitudeDifference / 2) ** 2;

                return (
                    earthRadiusKm *
                    2 *
                    Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
                );
            }
        }
    );
}